// ---------------------------------------------------------------------------
// banners-config — shared Banners page config, consumed by the strip-mode page
// (banners.js) and the grid-mode demo page (banners-grid.js). Only the
// `cardsSection` differs between them.
// ---------------------------------------------------------------------------

export const bannersBasePath = '/banners';

/** @typedef {Record<string, any>} Vals */

/**
 * Build the shared `createCrudPage` config. The caller supplies the
 * `cardsSection` (strip vs grid) so the table/fields/actions stay single-sourced.
 *
 * @param {import('./factories/crud-page.js').CardsSectionConfig} cardsSection
 * @returns {import('./factories/crud-page.js').CrudPageConfig}
 */
export function bannersConfig(cardsSection) {
  return {
    title: 'Banners',
    basePath: bannersBasePath,
    cardsSection,
    columns: [
      { key: 'banners_type_name', label: 'Type', sortable: true },
      { key: 'client_name', label: 'Client', sortable: true },
      { key: 'title', label: 'Title', sortable: true },
      { key: 'status', label: 'Active', sortable: true,
        render: (/** @type {unknown} */ v) => v == 1 ? 'Yes' : 'No' },
      { key: 'archive', label: 'Archive', sortable: true,
        render: (/** @type {unknown} */ v) => v == 1 ? 'Yes' : 'No' },
      { key: 'price', label: 'Price', sortable: true },
      { key: 'limit_by_label', label: 'Limit', sortable: true },
      { key: 'appearance', label: 'Views', sortable: true },
      { key: 'clicks', label: 'Clicks', sortable: true },
    ],
    fields: [
      { key: 'banners_type', label: 'Type', type: 'async-select',
        optionsUrl: '/banner-types/options',
        placeholder: 'Select type', required: true },
      { key: 'clients_pid', label: 'Client', type: 'async-select',
        optionsUrl: '/clients/options',
        placeholder: 'Select client' },
      { key: 'title', label: 'Title', type: 'text', required: true },
      { key: 'status', label: 'Active', type: 'yesno', defaultValue: '0' },
      { key: 'limit_by', label: 'Limit by', type: 'async-select',
        optionsUrl: '/banner-limit-types/options',
        placeholder: 'Select limit type' },
      { key: 'impressions', label: 'Number of views', type: 'text', defaultValue: '0',
        dependsOn: ['limit_by'],
        visible: (/** @type {Vals} */ v) => v.limit_by === '2' || v.limit_by === '3' },
      { key: 'sectors', label: 'Sector', type: 'multi-relation',
        engine: 'searchable-multi', optionsUrl: '/company-types/options',
        dependsOn: ['banners_type'],
        visible: (/** @type {Vals} */ v) => v.banners_type === '2' },
      { key: 'date_from', label: 'Date from', type: 'datetime',
        dependsOn: ['limit_by'],
        visible: (/** @type {Vals} */ v) => v.limit_by === '1' || v.limit_by === '3' },
      { key: 'date_to', label: 'Date to', type: 'datetime',
        dependsOn: ['limit_by'],
        visible: (/** @type {Vals} */ v) => v.limit_by === '1' || v.limit_by === '3' },
      { key: 'regions', label: 'Region', type: 'multi-relation',
        engine: 'searchable-multi', optionsUrl: '/regions/options' },
      { key: 'platform', label: 'Platform', type: 'async-select',
        optionsUrl: '/banner-platforms/options',
        placeholder: 'Select platform' },
      { key: 'square', label: 'Graphic', type: 'file', accept: 'image/*', photo: true },
      { key: 'price', label: 'Price per month', type: 'text' },
      { key: 'link_type', label: 'Link type', type: 'async-select',
        optionsUrl: '/banner-link-types/options',
        placeholder: 'Select link type' },
      { key: 'link', label: 'Link', type: 'text',
        dependsOn: ['link_type'],
        visible: (/** @type {Vals} */ v) => !!v.link_type,
        fieldInfo: 'For external links use full URL (e.g. https://example.com). For internal links enter article or catalog ID.' },
      { key: 'new_window', label: 'New window', type: 'yesno', defaultValue: '1' },
    ],
    filters: [
      { key: 'bannersType', queryKey: 'banners_type', label: 'Type', type: 'select',
        optionsUrl: '/banner-types/options' },
      { key: 'active', queryKey: 'status', label: 'Active', type: 'radio',
        options: [
          { value: '', label: 'All' },
          { value: '1', label: 'Yes' },
          { value: '0', label: 'No' },
        ] },
      { key: 'archive', label: 'Archive', type: 'radio',
        options: [
          { value: '', label: 'All' },
          { value: '1', label: 'Yes' },
          { value: '0', label: 'No' },
        ] },
    ],
    rowActions: [
      { key: 'edit', label: 'Edit' },
      { key: 'duplicate', label: 'Duplicate', icon: 'duplicate',
        confirm: 'Are you sure you want to duplicate this banner?' },
      { key: 'archive', label: 'Archive', icon: 'archive',
        confirm: 'Are you sure you want to archive this banner?',
        visible: (/** @type {Vals} */ row) => row.archive != 1 },
      { key: 'delete', label: 'Delete', variant: 'danger',
        confirm: 'Are you sure you want to remove this row?' },
    ],
    customActions: (/** @type {import('./factories/crud-page.js').ActionContext} */ ctx) => ({
      duplicate: async (/** @type {Vals} */ row) => {
        const id = String(row?.[ctx.rowKey] ?? '').trim();
        if (!id) return;
        const data = await ctx.adapter.get(id);
        const { id: _id, created: _created, appearance: _appearance, clicks: _clicks, ...rest } = data;
        ctx.editor.open(undefined, { prefill: { ...rest, status: 0, archive: 0 } });
      },
      archive: async (/** @type {Vals} */ row) => {
        const id = String(row?.[ctx.rowKey] ?? '').trim();
        if (!id) return;
        await ctx.services.apiClient.patch(
          `${bannersBasePath}/${encodeURIComponent(id)}`, { archive: 1 },
          { signal: ctx.services.signal },
        );
        ctx.grid.refresh(); // wrapped in grid mode → also refreshes the cards
      },
    }),
    defaultSort: { field: 'title', dir: 'asc' },
  };
}
